import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  LinearProgress,
  Chip,
  CircularProgress,
  Alert,
  Tooltip,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  Skeleton,
  Snackbar,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import Divider from "@mui/material/Divider";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../../store";
import axiosClient from "../../../api/AxiosClient";
import MarkdownRenderer from "../../MarkdownRenderer";
import styles from "../agent-response.module.scss";
import { recordAgentAction } from "../../../store/agentSlice";

type JobIntakeAgentProps = {
  parsedContent: any;
};

type JobIntakeApiMode = "section" | "question";

type InterviewRound = {
  round: number;
  name: string;
  panel: string[];
  evaluation: string;
};

type ScreeningQuestion = {
  question: string;
  reference_answer: string;
};

type SectionQuestion = {
  field_id: string;
  question: string;
  suggestion: string | string[] | InterviewRound[] | ScreeningQuestion[] | null;
  source: "JD" | "LLM (Generated)" | "System" | "HM Required";
};

type SectionData = {
  session_id: string;
  section_index: number;
  total_sections: number;
  progress_percent: number;
  section: string;
  is_last: boolean;
  completed: boolean;
  questions: SectionQuestion[];
};

type SummaryField = {
  field_id: string;
  field_label: string;
  value: string;
  source: string;
};

type SummarySection = {
  section_name: string;
  fields: SummaryField[];
};

const DEFAULT_TA_SCREENING_ROUND: InterviewRound = {
  round: 1,
  name: "TA Screening",
  panel: [],
  evaluation: "Culture fit, communication, and career alignment.",
};

const FRONTEND_HIDDEN_JOB_INTAKE_FIELDS = new Set(["bonus_structure", "work_model", "agreed_timeline"]);

const isVisibleJobIntakeQuestionField = (fieldId: string) =>
  fieldId !== "salary_range" && !FRONTEND_HIDDEN_JOB_INTAKE_FIELDS.has(fieldId);

const isVisibleJobIntakeSummaryField = (fieldId: string) =>
  !FRONTEND_HIDDEN_JOB_INTAKE_FIELDS.has(fieldId);

const LOCATION_HEADER_PATTERN = /^(?:work\s+location|job\s+location|location|office|based\s+in)\s*:?\s*$/i;
const INLINE_LOCATION_PATTERN = /^(?:work\s+location|job\s+location|location|office|based\s+in)\s*[:\-]\s*(.+)$/i;
const LOCATION_SENTENCE_PATTERNS = [
  /(?:^|[\n.])\s*(?:work\s+location|job\s+location|location|office)\s*[:\-]\s*([^\n.;|]+)/i,
  /(?:based|located)\s+in\s+([A-Za-z][^.;\n|]{1,80})/i,
];
const LOCATION_ROLE_WORDS_PATTERN =
  /\b(engineer|developer|manager|director|analyst|architect|consultant|specialist|coordinator|designer|scientist|recruiter|product|marketing|sales|operations|finance|hr|legal|lead|head|principal|intern)\b/i;
const LOCATION_NOISE_PATTERN =
  /^(?:about|overview|summary|role|job|company|responsibilities|requirements|qualifications|who\s+we\s+are|what\s+you(?:'| i)?ll\s+do)\b/i;

const cleanLocationCandidate = (value: string): string =>
  value
    .replace(/\uFFFD/g, " ")
    .replace(/^[\s,;:|\-]+|[\s,;:|]+$/g, "")
    .replace(/\s+/g, " ")
    .trim();

const isLikelyLocationValue = (value: string): boolean => {
  const candidate = cleanLocationCandidate(value);

  if (!candidate || candidate.length > 80 || LOCATION_NOISE_PATTERN.test(candidate)) {
    return false;
  }

  if (/[.!?]/.test(candidate) || candidate.split(/\s+/).length > 8) {
    return false;
  }

  if (/^[A-Z]{2,3}$/.test(candidate)) {
    return true;
  }

  if (/(remote|hybrid|on-?site|onsite)/i.test(candidate)) {
    return true;
  }

  if (candidate.includes(",")) {
    return /^[A-Za-z][A-Za-z.' -]+(?:,\s*[A-Za-z][A-Za-z.' -]+){1,3}(?:\s*\([^)]*\))?$/i.test(candidate);
  }

  if (/^[A-Za-z][A-Za-z.' -]{1,40}$/.test(candidate) && !LOCATION_ROLE_WORDS_PATTERN.test(candidate)) {
    return true;
  }

  return false;
};

const sanitizeLocationSuggestion = (rawSuggestion: string): string => {
  const raw = rawSuggestion.replace(/\r/g, "\n").trim();
  if (!raw) return "";

  const lines = raw
    .split("\n")
    .map(cleanLocationCandidate)
    .filter(Boolean);

  for (const line of lines) {
    const inlineMatch = line.match(INLINE_LOCATION_PATTERN);
    if (!inlineMatch) continue;

    const candidate = cleanLocationCandidate(inlineMatch[1]);
    if (isLikelyLocationValue(candidate)) {
      return candidate;
    }
  }

  for (let index = 0; index < lines.length; index += 1) {
    if (!LOCATION_HEADER_PATTERN.test(lines[index])) continue;

    const nextLine = lines.slice(index + 1).find(Boolean);
    if (nextLine && isLikelyLocationValue(nextLine)) {
      return nextLine;
    }
  }

  for (const pattern of LOCATION_SENTENCE_PATTERNS) {
    const match = raw.match(pattern);
    if (!match) continue;

    const candidate = cleanLocationCandidate(match[1]);
    if (isLikelyLocationValue(candidate)) {
      return candidate;
    }
  }

  const firstLine = lines[0];
  if (firstLine && isLikelyLocationValue(firstLine)) {
    return firstLine;
  }

  const compact = cleanLocationCandidate(raw.replace(/\n+/g, " "));
  return isLikelyLocationValue(compact) ? compact : "";
};

const normalizeInterviewRounds = (rounds: InterviewRound[]): InterviewRound[] => {
  const remainingRounds = rounds.slice(1).map((round, index) => ({
    round: index + 2,
    name: round?.name ?? "",
    panel: Array.isArray(round?.panel) ? round.panel : [],
    evaluation: round?.evaluation ?? "",
  }));

  return [DEFAULT_TA_SCREENING_ROUND, ...remainingRounds];
};

export default function JobIntakeAgent({
  parsedContent,
}: JobIntakeAgentProps): JSX.Element {
  const dispatch = useAppDispatch();
  const currentAgent = useSelector((state: RootState) => state.agents.currentAgent);
  const numericAgentId = currentAgent?.id || 11;
  const { selectedAgent, sessionId: globalSessionId } = useSelector(
    (state: RootState) => state.user
  );
  const authUser = useSelector((state: RootState) => state.auth.user);

  // Detect workflow type
  const [workflowType, setWorkflowType] = useState<"old" | "new">("old");

  // State management
  const [stage, setStage] = useState<"qa" | "email_capture" | "summary" | "email_preview">("qa");
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [apiMode, setApiMode] = useState<JobIntakeApiMode>("section");
  const [jobTitle, setJobTitle] = useState<string>("");
  const [totalSections, setTotalSections] = useState<number>(0);
  const [fileType, setFileType] = useState<string>("");
  const [recruiterEmails, setRecruiterEmails] = useState<string[]>([""]);
  const [hiringManagerEmail, setHiringManagerEmail] = useState<string>(authUser?.username || "");
  const [hiringManagerEmailError, setHiringManagerEmailError] = useState<string>("");
  const [emailData, setEmailData] = useState<{
    to: string;
    cc: string;
    subject: string;
    body: string;
    completed_at: string;
  } | null>(null);

  // Q&A stage state (section-based)
  const [currentSection, setCurrentSection] = useState<SectionData | null>(null);
  const [sectionAnswers, setSectionAnswers] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [qaError, setQaError] = useState<string>("");
  const [sectionHistory, setSectionHistory] = useState<{ section: SectionData; answers: Record<string, string> }[]>([]);
  const [sectionForwardStack, setSectionForwardStack] = useState<{ section: SectionData; answers: Record<string, string> }[]>([]);
  const [sectionErrors, setSectionErrors] = useState<Record<string, string>>({});

  // Summary stage state (section-wise navigation)
  const [summaryData, setSummaryData] = useState<SummarySection[]>([]);
  const [currentSectionIndex, setCurrentSectionIndex] = useState<number>(0);
  const [loadingSummary, setLoadingSummary] = useState<boolean>(false);
  const [summaryError, setSummaryError] = useState<string>("");
  const [emailError, setEmailError] = useState<string>("");
  const [editingField, setEditingField] = useState<{ sectionIdx: number; fieldIdx: number } | null>(null);
  const [editingValue, setEditingValue] = useState("");

  // Old workflow state (display all extracted data)
  const [extractedData, setExtractedData] = useState<any>(null);
  const [prompt, setPrompt] = useState<string>("");

  // Success snackbar after each section submit
  const [sectionSubmitSuccess, setSectionSubmitSuccess] = useState(false);

  // Refs for sticky summary section navigation
  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});

  // Navigation lock — prevents double-clicks / concurrent navigations.
  // Reset via useEffect (not synchronously) so the lock stays held until React
  // has committed the state change and the component's closures are fresh.
  const isNavigatingRef = useRef(false);

  // Ref attached to the top of the Q&A stage container, used to scroll
  // the chat window back to the top when a new section loads.
  const sectionTopRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    isNavigatingRef.current = false;
    // Reset scroll position so the start of the new section is immediately visible
    if (sectionTopRef.current) {
      let el: HTMLElement | null = sectionTopRef.current.parentElement;
      while (el) {
        const style = window.getComputedStyle(el);
        if (
          el.scrollHeight > el.clientHeight &&
          (style.overflowY === "auto" || style.overflowY === "scroll")
        ) {
          el.scrollTop = 0;
          break;
        }
        el = el.parentElement;
      }
    }
  }, [currentSection]);

  // Tracks which session_id is currently being initialized.  Updated synchronously
  // (unlike state) so a second effect call in the same tick sees the guard and skips.
  const initializingSessionRef = useRef<string | null>(null);

  const serializeSuggestion = (
    suggestion: SectionQuestion["suggestion"],
  ): string => {
    if (typeof suggestion === "string") return suggestion;
    if (Array.isArray(suggestion)) {
      if (suggestion.length === 0) return "";
      return typeof suggestion[0] === "object"
        ? JSON.stringify(suggestion)
        : "• " + (suggestion as string[]).join("\n• ");
    }
    return suggestion != null ? JSON.stringify(suggestion) : "";
  };

  const normalizeLegacyQuestionResponse = (
    data: any,
    sid: string,
  ): SectionData | null => {
    if (!data?.field_id || !data?.question) return null;

    return {
      session_id: data.session_id || sid,
      section_index: data.question_index ?? 0,
      total_sections: data.total_questions ?? 0,
      progress_percent: data.progress_percent ?? 0,
      section: data.section || `Question ${(data.question_index ?? 0) + 1}`,
      is_last: !!data.is_last,
      completed: !!data.completed,
      questions: [
        {
          field_id: data.field_id,
          question: data.question,
          suggestion: data.suggestion ?? null,
          source: data.source || "HM Required",
        },
      ],
    };
  };

  const resetSessionUiState = () => {
    setCurrentSection(null);
    setSectionAnswers({});
    setSectionHistory([]);
    setSectionForwardStack([]);
    setSectionErrors({});
    setSummaryData([]);
    setCurrentSectionIndex(0);
    setLoadingSummary(false);
    setSummaryError("");
    setEmailData(null);
    setEmailError("");
    setQaError("");
    setRecruiterEmails([""]);
    setHiringManagerEmail(authUser?.username || "");
    setHiringManagerEmailError("");
    setEditingField(null);
    setEditingValue("");
  };

  // Parse initial response if coming from backend
  useEffect(() => {
    // Start with the raw content
    let result = parsedContent?.finalAnswer || parsedContent;

    // Try to parse if it's a JSON string
    if (typeof result === "string") {
      try {
        result = JSON.parse(result);
      } catch (e) {
        // Not JSON, keep as is
      }
    }

    console.log("JobIntakeAgent - Raw parsedContent:", parsedContent);
    console.log("JobIntakeAgent - Parsed result:", result);

    // NEW WORKFLOW: Check if this is an upload response from /stream/job-intake/upload.
    // Support both the current section-based contract (`total_sections`) and
    // the older question-based contract (`total_questions`).
    const detectedApiMode: JobIntakeApiMode | null = result?.total_sections
      ? "section"
      : result?.total_questions
        ? "question"
        : null;

    if (result?.session_id && detectedApiMode) {
      console.log(
        `✅ NEW workflow: Upload response detected (${detectedApiMode}-based)`
      );

      // Guard: skip if already handling this session_id.
      // initializingSessionRef is updated synchronously so even a second same-tick
      // effect call (from a parent re-render) sees the guard and skips, preventing
      // the duplicate /next call visible in server logs.
      if (sessionId !== result.session_id && initializingSessionRef.current !== result.session_id) {
        initializingSessionRef.current = result.session_id;
        resetSessionUiState();
        setApiMode(detectedApiMode);
        setSessionId(result.session_id);
        setJobTitle(result.job_title || "");
        setTotalSections(result.total_sections || result.total_questions || 0);
        setFileType(result.file_type || "");
        setStage("qa");
        setWorkflowType("new");
        // Fetch first section
        fetchNextSection(result.session_id, detectedApiMode);
      }
      return;
    }

    // OLD WORKFLOW: Check if this is the answer format (answer field with JSON string)
    // Check this FIRST before any data extraction
    if (result?.answer && typeof result.answer === "string") {
      try {
        const parsedAnswer = JSON.parse(result.answer);
        setExtractedData(parsedAnswer);
        setPrompt(result.question || "");
        setWorkflowType("old");
        setJobTitle(parsedAnswer.job_title || "");
        console.log("Old workflow detected (answer field), parsed data:", parsedAnswer);
        return; // Exit early
      } catch (e) {
        console.error("Failed to parse answer JSON:", e);
        setQaError("Failed to parse job intake data.");
        return;
      }
    }

    // If not answer format, check if data is nested deeper
    if (result?.result?.data) {
      result = result.result.data;
    } else if (result?.data?.output) {
      result = result.data;
    } else if (result?.data) {
      result = result.data;
    }

    console.log("JobIntakeAgent - Extracted result:", result);

    // OLD WORKFLOW: Check if this is the old format (output as JSON string)
    if (result?.output && typeof result.output === "string") {
      try {
        const parsedOutput = JSON.parse(result.output);
        setExtractedData(parsedOutput);
        setPrompt(result.prompt || "");
        setWorkflowType("old");
        setJobTitle(parsedOutput.job_title || "");
        console.log("Old workflow detected (output field), parsed output:", parsedOutput);
        return;
      } catch (e) {
        console.error("Failed to parse output JSON:", e);
        setQaError("Failed to parse job intake data.");
        return;
      }
    }
    // OLD WORKFLOW: Check if output is already an object
    if (result?.output && typeof result.output === "object") {
      setExtractedData(result.output);
      setPrompt(result.prompt || "");
      setWorkflowType("old");
      setJobTitle(result.output.job_title || "");
      console.log("Old workflow detected (object format):", result.output);
      return;
    }
    // NEW WORKFLOW: Check if completed
    if (result?.completed === true) {
      setStage("email_capture");
      setWorkflowType("new");
      setSessionId(result.session_id || sessionId);
      console.log("New workflow detected (completed)");
      return;
    }
    // If we reach here and still no data, log for debugging
    {
      console.warn("JobIntakeAgent - Unrecognized data structure:", {
        parsedContent,
        result,
        hasOutput: !!result?.output,
        hasSessionId: !!result?.session_id,
        hasSections: !!result?.total_sections,
        hasQuestions: !!result?.total_questions,
      });
    }
  }, [parsedContent]);

  // ========== Q&A HANDLERS (NEW WORKFLOW - SECTION-BASED) ==========

  const fetchNextSection = async (
    sid: string,
    modeHint?: JobIntakeApiMode,
  ) => {
    setLoading(true);
    setQaError("");

    try {
      const response = await axiosClient.get(
        `/stream/job-intake/next?session_id=${sid}`
      );

      const data = response.data;

      // Check if completed
      if (data.completed) {
        setStage("email_capture");
      } else {
        const normalized = Array.isArray(data.questions)
          ? (data as SectionData)
          : normalizeLegacyQuestionResponse(data, sid);

        if (!normalized) {
          console.warn("JobIntakeAgent - Unsupported /next response:", data);
          setQaError("Received an unsupported job intake response format.");
          return;
        }

        // Clear forward stack since we're taking a new path
        setSectionForwardStack([]);
        setApiMode(modeHint || (Array.isArray(data.questions) ? "section" : "question"));
        setTotalSections(normalized.total_sections || 0);

        const visibleQuestions = normalized.questions.filter((q: SectionQuestion) =>
          isVisibleJobIntakeQuestionField(q.field_id)
        );
        const visibleSection = { ...normalized, questions: visibleQuestions };

        // Pre-fill sectionAnswers: use suggestion for non-HM-Required fields
        const prefilled: Record<string, string> = {};
        visibleQuestions.forEach((q: SectionQuestion) => {
          if (q.field_id === "target_start_date") {
            // Always default to today + 90 days if no suggestion provided
            const suggestion = q.suggestion as string | null;
            prefilled[q.field_id] =
              suggestion && suggestion.trim()
                ? suggestion
                : dayjs().add(90, "day").format("YYYY-MM-DD");
          } else if (q.field_id === "interview_rounds" && Array.isArray(q.suggestion)) {
            // Round 1 stays a TA-owned default; HM only fills later rounds.
            const rounds = normalizeInterviewRounds(q.suggestion as InterviewRound[]).map((round, index) =>
              index === 0 ? round : { ...round, panel: [] }
            );
            prefilled[q.field_id] = JSON.stringify(rounds);
          } else if (q.source !== "HM Required" && q.suggestion != null) {
            const serialized = serializeSuggestion(q.suggestion);
            prefilled[q.field_id] =
              q.field_id === "location"
                ? sanitizeLocationSuggestion(serialized)
                : serialized;
          } else {
            prefilled[q.field_id] = "";
          }
        });

        // If nothing remains visible in this section, or all visible questions
        // are auto-generated, silently advance without user interaction.
        const hasHMRequired = visibleQuestions.some(
          (q: SectionQuestion) => q.source === "HM Required"
        );
        if (visibleQuestions.length === 0 || !hasHMRequired) {
          try {
            const responses: Record<string, { action: string; value?: string }> = {};
            visibleQuestions.forEach((q: SectionQuestion) => {
              const userVal = (prefilled[q.field_id] ?? "").trim();
              if (!userVal) return;
              const isUnchanged =
                userVal === serializeSuggestion(q.suggestion) &&
                q.source !== "HM Required";
              responses[q.field_id] = isUnchanged
                ? { action: "confirm" }
                : { action: "edit", value: userVal };
            });
            await axiosClient.post("/stream/job-intake/respond", {
              session_id: sid,
              responses,
            });
          } catch (autoError: any) {
            console.error("Auto-confirm section error:", autoError);
            setQaError(
              autoError.response?.data?.detail ||
                "Failed to process section. Please try again."
            );
            return;
          }
          await fetchNextSection(sid, modeHint);
          return;
        }

        setSectionErrors({});
        setCurrentSection(visibleSection);
        setSectionAnswers(prefilled);
      }
    } catch (error: any) {
      console.error("Fetch section error:", error);
      setQaError(
        error.response?.data?.detail ||
          "Failed to fetch section. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const validateSection = (): boolean => {
    if (!currentSection) return true;
    const errors: Record<string, string> = {};
    currentSection.questions.filter((q) => isVisibleJobIntakeQuestionField(q.field_id)).forEach((q) => {
      const requiresAnswer =
        apiMode === "question"
          ? !(sectionAnswers[q.field_id] ?? "").trim()
          : q.source === "HM Required" && !(sectionAnswers[q.field_id] ?? "").trim();
      if (requiresAnswer) {
        const val = (sectionAnswers[q.field_id] ?? "").trim();
        if (!val) errors[q.field_id] = "This field is required";
      }
    });
    setSectionErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmitSection = async () => {
    if (!sessionId || !currentSection) return;
    if (isNavigatingRef.current) return; // prevent double-click concurrent navigations
    isNavigatingRef.current = true;

    if (!validateSection()) {
      isNavigatingRef.current = false;
      return;
    }

    // If there are cached forward sections, navigate through them without API call
    if (sectionForwardStack.length > 0) {
      console.log("➡️ Moving forward through section history");

      // Capture current stack snapshot (closure value is safe here — lock prevents
      // a second call from entering until useEffect resets it after re-render)
      const nextEntry = sectionForwardStack[sectionForwardStack.length - 1];

      setSectionHistory((prev) => [
        ...prev,
        { section: currentSection, answers: sectionAnswers },
      ]);
      setSectionForwardStack((prev) => prev.slice(0, -1));

      setCurrentSection(nextEntry.section);
      setSectionAnswers(nextEntry.answers);
      // lock released by useEffect([currentSection]) after React commits
      return;
    }

    // No forward stack — submit to backend
    setLoading(true);
    setQaError("");

    try {
      const visibleQuestions = currentSection.questions.filter((q) =>
        isVisibleJobIntakeQuestionField(q.field_id)
      );
      const legacyQuestion = visibleQuestions[0];
      let requestBody: Record<string, any>;

      if (apiMode === "question" && legacyQuestion) {
        const userVal = (sectionAnswers[legacyQuestion.field_id] ?? "").trim();
        const isUnchanged =
          userVal === serializeSuggestion(legacyQuestion.suggestion) &&
          legacyQuestion.source !== "HM Required";

        requestBody = isUnchanged
          ? { session_id: sessionId, action: "confirm" }
          : { session_id: sessionId, action: "edit", value: userVal };
      } else {
        const responses: Record<string, { action: string; value?: string }> = {};
        visibleQuestions.forEach((q) => {
          const userVal = (sectionAnswers[q.field_id] ?? "").trim();
          if (!userVal) return; // omit blank fields
          const isUnchanged =
            userVal === serializeSuggestion(q.suggestion) &&
            q.source !== "HM Required";
          responses[q.field_id] = isUnchanged
            ? { action: "confirm" }
            : { action: "edit", value: userVal };
        });

        requestBody = {
          session_id: sessionId,
          responses,
        };
      }

      // Save current section to history before moving forward
      setSectionHistory((prev) => [
        ...prev,
        { section: currentSection, answers: sectionAnswers },
      ]);

      const response = await axiosClient.post(
        "/stream/job-intake/respond",
        requestBody
      );

      const data = response.data;
      console.log("✅ Section submit response:", data);

      if (data.completed) {
        console.log("🎉 Job intake questions completed! Moving to email capture...");
        setStage("email_capture");
      } else {
        await fetchNextSection(sessionId);
        setSectionSubmitSuccess(true);
      }
    } catch (error: any) {
      console.error("❌ Section submit error:", error);
      // Roll back history push on error
      setSectionHistory((prev) => prev.slice(0, -1));
      setQaError(
        error.response?.data?.detail ||
          "Failed to submit section. Please try again."
      );
    } finally {
      setLoading(false);
      isNavigatingRef.current = false;
    }
  };

  const handlePreviousSection = () => {
    if (sectionHistory.length === 0 || !currentSection) return;
    if (isNavigatingRef.current) return; // prevent double-click concurrent navigations
    isNavigatingRef.current = true;

    console.log("⬅️ Moving to previous section");

    // Reading sectionHistory from closure is safe here — isNavigatingRef stays
    // true until useEffect([currentSection]) fires after React commits, so no
    // second call can enter with a stale closure before re-render.
    const previousEntry = sectionHistory[sectionHistory.length - 1];

    setSectionForwardStack((prev) => [
      ...prev,
      { section: currentSection, answers: sectionAnswers },
    ]);
    setSectionHistory((prev) => prev.slice(0, -1));

    // Restore section and the answers the user had entered
    setCurrentSection(previousEntry.section);
    setSectionAnswers(previousEntry.answers);
    // lock released by useEffect([currentSection]) after React commits
  };

  // Save an inline-edited summary field
  const handleSaveFieldEdit = () => {
    if (!editingField) return;
    const { sectionIdx, fieldIdx } = editingField;
    setSummaryData((prev) =>
      prev.map((section, si) =>
        si !== sectionIdx
          ? section
          : {
              ...section,
              fields: section.fields.map((f, fi) =>
                fi !== fieldIdx ? f : { ...f, value: editingValue }
              ),
            }
      )
    );
    setEditingField(null);
  };

  // Build email body from current (possibly edited) summaryData, then show preview
  const handleDraftEmail = () => {
    const newBody = summaryData
      .map((section, idx) => {
        const header = `${idx + 1}. ${section.section_name.toUpperCase()}`;
        const fields = section.fields
          .map((f) => `**${f.field_label}:** ${f.value || "Not provided"}`)
          .join("\n");
        return `${header}\n${fields}`;
      })
      .join("\n\n");
    setEmailData((prev) => (prev ? { ...prev, body: newBody } : prev));
    setStage("email_preview");
  };

  // Maps current stage to a 0-based stepper index
  const getActiveStep = () => {
    switch (stage) {
      case "qa":            return 1;
      case "email_capture": return 2;
      case "summary":       return 2;
      case "email_preview": return 3;
      default:              return 1;
    }
  };

  // ========== SUMMARY HANDLERS (NEW WORKFLOW) ==========

  const fetchSummary = async (sid: string, email: string, ccEmail: string) => {
    console.log("📊 Fetching summary for session:", sid, "with email:", email);
    setLoadingSummary(true);
    setSummaryError("");
    setSummaryData([]);
    setQaError("");

    try {
      const summaryUrl = `/stream/job-intake/summary?session_id=${sid}&recruiter_email=${encodeURIComponent(email)}&cc_email=${encodeURIComponent(ccEmail)}`;
      console.log("📊 Summary URL:", summaryUrl);

      const response = await axiosClient.get(summaryUrl);

      const data = response.data;
      console.log("✅ Summary response:", data);

      // Extract email data
      if (data.to && data.subject && data.body) {
        setEmailData({
          to: data.to,
          cc: data.cc || ccEmail,
          subject: data.subject,
          body: data.body,
          completed_at: data.completed_at || new Date().toISOString(),
        });
        console.log("📧 Email data extracted:", {
          to: data.to,
          subject: data.subject,
        });
      }

      // Parse the fields_with_sources object into sections
      const sections = parseFieldsWithSources(data.fields_with_sources || {});
      console.log("📋 Parsed sections:", sections);
      setSummaryData(sections);
      setCurrentSectionIndex(0);
    } catch (error: any) {
      console.error("❌ Fetch summary error:", error);
      console.error("❌ Error details:", error.response?.data);
      const detail =
        error.response?.data?.detail ||
        "Failed to fetch summary. Please try again.";

      if (
        typeof detail === "string" &&
        detail.toLowerCase().startsWith("intake not complete")
      ) {
        setStage("qa");
        await fetchNextSection(sid, apiMode);
        setQaError(detail);
        return;
      }

      setSummaryError(detail);
      setQaError(detail);
    } finally {
      setLoadingSummary(false);
    }
  };

  // Field metadata mapping — aligned with 11-section backend structure
  const fieldMetadata: Record<string, { label: string; section: string }> = {
    // 1. Role Overview
    job_title:          { label: "Job Title",               section: "Role Overview" },
    department:         { label: "Department / Team",        section: "Role Overview" },
    location:           { label: "Location",                 section: "Role Overview" },
    reporting_to:       { label: "Reporting To",             section: "Role Overview" },
    reason_for_opening: { label: "Reason for Opening",       section: "Role Overview" },
    target_start_date:  { label: "Target Start Date",        section: "Role Overview" },
    // 2. Success Criteria
    success_criteria:   { label: "Top Outcomes (6–12 months)", section: "Success Criteria" },
    kpis:               { label: "Key KPIs",                   section: "Success Criteria" },
    // 3. Must-Have vs. Nice-to-Have Skills
    must_have_skills:   { label: "Must-Have Skills / Experience",   section: "Must-Have vs. Nice-to-Have Skills" },
    nice_to_have_skills:{ label: "Nice-to-Have Skills / Experience", section: "Must-Have vs. Nice-to-Have Skills" },
    experience:         { label: "Years of Experience",      section: "Must-Have vs. Nice-to-Have Skills" },
    education:          { label: "Education",                section: "Must-Have vs. Nice-to-Have Skills" },
    // 4. Team & Culture Fit
    team_structure:     { label: "Team Size and Structure",  section: "Team & Culture Fit" },
    stakeholders:       { label: "Key Stakeholders",         section: "Team & Culture Fit" },
    working_style:      { label: "Preferred Working Style",  section: "Team & Culture Fit" },
    cultural_traits:    { label: "Culture Traits",           section: "Team & Culture Fit" },
    // 5. Compensation & Logistics
    salary_range:       { label: "Salary Range / Grade Level", section: "Compensation & Logistics" },
    bonus_structure:    { label: "Bonus / Incentives",       section: "Compensation & Logistics" },
    work_model:         { label: "Work Model",               section: "Compensation & Logistics" },
    work_schedule:      { label: "Work Schedule Expectations", section: "Compensation & Logistics" },
    // 6. Sourcing Strategy
    target_profiles:    { label: "Ideal Candidate Backgrounds", section: "Sourcing Strategy" },
    geography:          { label: "Target Locations",           section: "Sourcing Strategy" },
    internal_candidates:{ label: "Internal Candidates",        section: "Sourcing Strategy" },
    target_companies:   { label: "Target Companies",           section: "Sourcing Strategy" },
    // 7. Screening & Interview Process
    ta_screening_focus: { label: "Recruiter Screening Criteria", section: "Screening & Interview Process" },
    estimated_timeline: { label: "Estimated Timeline",           section: "Screening & Interview Process" },
    interview_rounds:   { label: "Interview Rounds",             section: "Screening & Interview Process" },
    // 8. Screening Questions
    screening_questions: { label: "Screening Questions", section: "Screening Questions" },
    // 9. Employer Value Proposition
    employer_value_proposition: { label: "Why a Candidate Would Want This Job", section: "Employer Value Proposition" },
    compelling_aspects:         { label: "What Makes Team / Project Compelling", section: "Employer Value Proposition" },
    growth_opportunities:       { label: "Growth Opportunities",                 section: "Employer Value Proposition" },
    // 9. Red Flags
    red_flags:          { label: "Poor Fit Indicators",      section: "Red Flags" },
    // 10. Next Steps & Alignment
    agreed_timeline:    { label: "Agreed Timeline for Updates",       section: "Next Steps & Alignment" },
    hm_availability:    { label: "Hiring Manager Availability",       section: "Next Steps & Alignment" },
    materials_needed:   { label: "Materials Needed",                  section: "Next Steps & Alignment" },
    // 11. Referrals
    referrals:          { label: "Referrals",                section: "Referrals" },
  };

  // Sample answer hints shown via the (i) tooltip on each Q&A question
  const fieldHints: Record<string, string> = {
    job_title:                  'e.g. "Senior Data Engineer" or "AI Product Manager"',
    department:                 'e.g. "Technology & Data" or "AI Centre of Excellence"',
    location:                   'e.g. "Abu Dhabi, UAE (on-site)" or "Dubai – hybrid (3 days in office)"',
    reporting_to:               'e.g. "Head of Data Engineering" or "VP of Product"',
    reason_for_opening:         'e.g. "New headcount approved for Q3 growth" or "Backfill – previous employee promoted internally"',
    target_start_date:          'e.g. "2025-09-01" — allow 3–6 months for sourcing, interviews, approvals, and notice period',
    success_criteria:           'e.g. "Own and deliver the data lakehouse migration within 6 months; onboard 3 downstream teams onto new pipelines"',
    kpis:                       'e.g. "Pipeline uptime >99.5%; data freshness <1 hour; stakeholder satisfaction score >4/5"',
    must_have_skills:           'e.g. "5+ years Python; production experience with dbt and Spark; strong SQL"',
    nice_to_have_skills:        'e.g. "Experience with Databricks; familiarity with UAE data regulations; Arabic language skills"',
    experience:                 'e.g. "5–8 years of relevant experience; at least 3 years in a senior individual contributor role"',
    education:                  'e.g. "Bachelor\'s in Computer Science or equivalent; Master\'s preferred but not required"',
    team_structure:             'e.g. "Joins a team of 6 engineers; works closely with 2 data analysts and 1 ML engineer"',
    stakeholders:               'e.g. "Finance, Risk, and Product teams; reports to CTO; collaborates with external cloud vendor"',
    working_style:              'e.g. "Collaborative, outcome-driven; comfortable with ambiguity in a fast-moving environment"',
    cultural_traits:            'e.g. "Ownership mindset; continuous learner; values diverse perspectives"',
    salary_range:               'e.g. "AED 25,000–35,000/month all-inclusive" or "Grade L5 – details shared at offer stage"',
    bonus_structure:            'e.g. "Up to 15% annual performance bonus based on individual and company targets"',
    work_model:                 'e.g. "Hybrid – 3 days in office (Abu Dhabi HQ), 2 days remote"',
    work_schedule:              'e.g. "Standard business hours (Sun–Thu, 09:00–18:00 GST); occasional weekend support during releases"',
    target_profiles:            'e.g. "Data engineers from fintech or telco; ex-FAANG engineers; G42 alumni"',
    geography:                  'e.g. "UAE-based preferred; open to candidates willing to relocate from GCC or Europe"',
    internal_candidates:        'e.g. "No current internal pipeline" or "2 internal candidates to be considered first"',
    target_companies:           'e.g. "Careem, Noon, Etisalat, Amazon, Google, McKinsey"',
    ta_screening_focus:         'e.g. "Must have led at least one large-scale migration; verify hands-on coding; check stability of tenure"',
    estimated_timeline:         'e.g. "Offer by end of August 2025; 4-week sourcing → 2-week interviews → 1-week approvals"',
    interview_rounds:           'e.g. Round 1: Recruiter screen (30 min) → Round 2: Technical panel (90 min) → Round 3: HM culture fit (60 min)',
    screening_questions:        'e.g. Q: "Describe a data pipeline you built from scratch." Reference answer: Look for ownership, scale, and lessons learned.',
    employer_value_proposition: 'e.g. "High-impact role with direct executive visibility; opportunity to work on cutting-edge AI infrastructure"',
    compelling_aspects:         'e.g. "Small, high-ownership team; greenfield architecture project; direct access to senior leadership"',
    growth_opportunities:       'e.g. "Fast track to Principal Engineer within 18–24 months; exposure to G42 group-wide initiatives"',
    red_flags:                  'e.g. "No hands-on coding in the last 2 years; history of very short tenures (<1 year); unable to explain design decisions"',
    agreed_timeline:            'e.g. "Recruiter to provide weekly shortlist updates every Monday; HM feedback within 48 hours of interview"',
    hm_availability:            'e.g. "Available Mon/Wed afternoons and Friday mornings for interviews in July"',
    materials_needed:           'e.g. "Updated JD for job board; technical assessment from engineering team; offer letter template from HR"',
    referrals:                  'e.g. "Ahmed Al Mansoori (LinkedIn: /in/ahmed) – ex-colleague at Careem; strong data engineering background"',
  };

  const parseFieldsWithSources = (fieldsWithSources: Record<string, any>): SummarySection[] => {
    const sectionMap: { [key: string]: SummaryField[] } = {};

    Object.entries(fieldsWithSources).forEach(([fieldId, fieldData]) => {
      if (!isVisibleJobIntakeSummaryField(fieldId)) return;

      const metadata = fieldMetadata[fieldId] || {
        label: fieldId.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase()),
        section: "Other",
      };

      const section = metadata.section;
      if (!sectionMap[section]) {
        sectionMap[section] = [];
      }

      // Handle both array and string values
      let displayValue = fieldData.value;
      if (fieldId === "screening_questions") {
        try {
          const questions: ScreeningQuestion[] = typeof displayValue === "string"
            ? JSON.parse(displayValue)
            : displayValue;
          if (Array.isArray(questions)) {
            displayValue = questions.map((q, i) =>
              `Q${i + 1}: ${q.question}\nReference Answer: ${q.reference_answer}`
            ).join("\n\n");
          }
        } catch {
          // keep as-is
        }
      } else if (fieldId === "interview_rounds") {
        try {
          const rounds: InterviewRound[] = typeof displayValue === "string"
            ? JSON.parse(displayValue)
            : displayValue;
          if (Array.isArray(rounds)) {
            displayValue = rounds.map((r) => {
              const panelStr = (r.panel || []).join(", ");
              return `Round ${r.round} — ${r.name}${panelStr ? `\n  Panel: ${panelStr}` : ""}\n  Evaluates: ${r.evaluation || ""}`;
            }).join("\n\n");
          }
        } catch {
          // keep as-is
        }
      } else if (Array.isArray(displayValue)) {
        displayValue = displayValue.length > 0
          ? "• " + displayValue.filter(Boolean).join("\n• ")
          : null;
      } else if (typeof displayValue !== "string") {
        displayValue = displayValue != null ? JSON.stringify(displayValue) : null;
      }

      // Skip null/empty fields — keep summary clean
      const trimmed = typeof displayValue === "string" ? displayValue.trim() : displayValue;
      if (!trimmed) return;

      sectionMap[section].push({
        field_id: fieldId,
        field_label: metadata.label,
        value: trimmed as string,
        source: fieldData.source || "System",
      });
    });

    // Define section order — matches 11-section backend structure
    const sectionOrder = [
      "Role Overview",
      "Success Criteria",
      "Must-Have vs. Nice-to-Have Skills",
      "Team & Culture Fit",
      "Compensation & Logistics",
      "Sourcing Strategy",
      "Screening & Interview Process",
      "Screening Questions",
      "Employer Value Proposition",
      "Red Flags",
      "Next Steps & Alignment",
      "Referrals",
      "Other",
    ];

    // Sort sections by defined order, skip sections with no filled fields
    const sortedSections = sectionOrder
      .filter((sectionName) => sectionMap[sectionName]?.length > 0)
      .map((sectionName) => ({
        section_name: sectionName,
        fields: sectionMap[sectionName],
      }));

    return sortedSections;
  };

  const handleNextSection = () => {
    if (currentSectionIndex < summaryData.length - 1) {
      setCurrentSectionIndex(currentSectionIndex + 1);
    }
  };

  const handlePreviousSummarySection = () => {
    if (currentSectionIndex > 0) {
      setCurrentSectionIndex(currentSectionIndex - 1);
    }
  };

  const retrySummaryFetch = () => {
    if (!sessionId) return;

    const recruiterEmailList = recruiterEmails
      .map((email) => email.trim())
      .filter(Boolean)
      .join(", ");

    fetchSummary(sessionId, recruiterEmailList, hiringManagerEmail.trim());
  };

  // ========== RENDER FUNCTIONS (OLD WORKFLOW) ==========

  const renderOldWorkflow = () => {
    if (!extractedData) {
      return (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <CircularProgress sx={{ color: "#00d2aa" }} />
          <Typography variant="body1" sx={{ mt: 2, color: "#666" }}>
            Processing job description...
          </Typography>
        </Box>
      );
    }

    return renderExtractedData();
  };

  const renderExtractedData = () => {
    const {
      job_title,
      must_have_skills,
      nice_to_have_skills,
      experience,
      education,
      location,
      responsibilities,
      department,
      success_criteria,
      kpis,
      candidate_profile,
      extraction_success,
      generation_success,
    } = extractedData;
    const displayLocation =
      typeof location === "string" ? sanitizeLocationSuggestion(location) : "";

    const textareaStyle = {
      width: "100%",
      padding: "16px",
      fontSize: "14px",
      lineHeight: "1.8",
      border: "1px solid #d1d5db",
      borderRadius: "8px",
      backgroundColor: "#ffffff",
      fontFamily: "inherit",
      resize: "vertical" as const,
    };

    const inputStyle = {
      width: "100%",
      padding: "12px 16px",
      fontSize: "14px",
      border: "1px solid #d1d5db",
      borderRadius: "8px",
      backgroundColor: "#ffffff",
      fontFamily: "inherit",
    };

    const sectionStyle = {
      marginBottom: "30px",
    };

    const headingStyle = {
      color: "#00d2aa",
      marginBottom: "12px",
      fontSize: "18px",
      fontWeight: "600" as const,
    };

    return (
      <>
        {/* Job Details Section */}
        {(job_title || experience || displayLocation || department) && (
          <div
            style={{
              ...sectionStyle,
              backgroundColor: "#f8f9fa",
              padding: "20px",
              borderRadius: "8px",
            }}
          >
            <h3 style={{ ...headingStyle, marginTop: "0" }}>Job Details</h3>

            {job_title && (
              <div style={{ marginBottom: "16px" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "6px",
                    fontWeight: "500",
                    fontSize: "14px",
                  }}
                >
                  Job Title
                </label>
                <input
                  type="text"
                  value={job_title}
                  readOnly
                  style={inputStyle}
                />
              </div>
            )}

            <div
              style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}
            >
              {experience && (
                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "6px",
                      fontWeight: "500",
                      fontSize: "14px",
                    }}
                  >
                    Experience Required
                  </label>
                  <input
                    type="text"
                    value={experience}
                    readOnly
                    style={inputStyle}
                  />
                </div>
              )}

              {department && (
                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "6px",
                      fontWeight: "500",
                      fontSize: "14px",
                    }}
                  >
                    Department
                  </label>
                  <input
                    type="text"
                    value={department}
                    readOnly
                    style={inputStyle}
                  />
                </div>
              )}

              {displayLocation && (
                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "6px",
                      fontWeight: "500",
                      fontSize: "14px",
                    }}
                  >
                    Location
                  </label>
                  <input
                    type="text"
                    value={displayLocation}
                    readOnly
                    style={inputStyle}
                  />
                </div>
              )}


            </div>
          </div>
        )}

        {/* Must-Have Skills */}
        {must_have_skills && Array.isArray(must_have_skills) && must_have_skills.length > 0 && (
          <div style={sectionStyle}>
            <h3 style={headingStyle}>Must-Have Skills</h3>
            <textarea
              value={must_have_skills.join("\n")}
              readOnly
              style={{ ...textareaStyle, minHeight: "150px" }}
            />
          </div>
        )}

        {/* Nice-to-Have Skills */}
        {nice_to_have_skills && Array.isArray(nice_to_have_skills) && nice_to_have_skills.length > 0 && (
          <div style={sectionStyle}>
            <h3 style={headingStyle}>Nice-to-Have Skills</h3>
            <textarea
              value={nice_to_have_skills.join("\n")}
              readOnly
              style={{ ...textareaStyle, minHeight: "100px" }}
            />
          </div>
        )}

        {/* Responsibilities */}
        {responsibilities && Array.isArray(responsibilities) && responsibilities.length > 0 && (
          <div style={sectionStyle}>
            <h3 style={headingStyle}>Responsibilities</h3>
            <textarea
              value={responsibilities.join("\n")}
              readOnly
              style={{ ...textareaStyle, minHeight: "150px" }}
            />
          </div>
        )}

        {/* Success Criteria Section */}
        {success_criteria && Array.isArray(success_criteria) && success_criteria.length > 0 && (
          <div style={sectionStyle}>
            <h3 style={headingStyle}>Success Criteria</h3>
            <textarea
              value={success_criteria.join("\n\n")}
              readOnly
              style={{ ...textareaStyle, minHeight: "200px" }}
            />
          </div>
        )}

        {/* KPIs Section */}
        {kpis && Array.isArray(kpis) && kpis.length > 0 && (
          <div style={sectionStyle}>
            <h3 style={headingStyle}>Key Performance Indicators (KPIs)</h3>
            <textarea
              value={kpis.join("\n\n")}
              readOnly
              style={{ ...textareaStyle, minHeight: "200px" }}
            />
          </div>
        )}

        {/* Candidate Profile Section */}
        {candidate_profile && (
          <div style={sectionStyle}>
            <h3 style={headingStyle}>Ideal Candidate Profile</h3>
            <textarea
              value={candidate_profile}
              readOnly
              style={{ ...textareaStyle, minHeight: "150px" }}
            />
          </div>
        )}

        {/* Display the prompt at the end */}
        {prompt && (
          <div style={{ marginTop: "30px", marginBottom: "20px" }}>
            <MarkdownRenderer content={prompt} />
          </div>
        )}

        {/* Error Messages */}
        {(!generation_success || !extraction_success) && (
          <div
            style={{
              backgroundColor: "#fff3cd",
              borderLeft: "4px solid #ffc107",
              padding: "12px 16px",
              borderRadius: "4px",
              marginTop: "20px",
            }}
          >
            <strong>Note:</strong>{" "}
            {!extraction_success && "Job data extraction may be incomplete. "}
            {!generation_success &&
              "Some fields may not have been generated successfully. "}
            Please review the output above.
          </div>
        )}
      </>
    );
  };

  // ========== RENDER FUNCTIONS (NEW WORKFLOW) ==========

  const renderEmailCaptureStage = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const completionLabel =
      apiMode === "question" ? "Questions Complete!" : "Sections Complete!";

    const handleEmailSubmit = () => {
      let hasError = false;

      const filledEmails = recruiterEmails.map((e) => e.trim()).filter(Boolean);
      if (filledEmails.length === 0) {
        setEmailError("Please enter at least one recruiter email address.");
        hasError = true;
      } else if (filledEmails.some((e) => !emailRegex.test(e))) {
        setEmailError("One or more recruiter email addresses are invalid.");
        hasError = true;
      }

      if (!hiringManagerEmail.trim()) {
        setHiringManagerEmailError("Please enter the hiring manager's email address.");
        hasError = true;
      } else if (!emailRegex.test(hiringManagerEmail)) {
        setHiringManagerEmailError("Please enter a valid email address.");
        hasError = true;
      }

      if (hasError) return;

      if (sessionId) {
        setStage("summary");
        fetchSummary(sessionId, filledEmails.join(", "), hiringManagerEmail);
      }
    };

    const updateRecruiterEmail = (index: number, value: string) => {
      const updated = recruiterEmails.map((e, i) => (i === index ? value : e));
      setRecruiterEmails(updated);
      // Real-time: clear error only when all filled emails become valid
      if (emailError) {
        const filled = updated.map((e) => e.trim()).filter(Boolean);
        if (filled.length > 0 && filled.every((e) => emailRegex.test(e))) {
          setEmailError("");
        }
      }
    };

    const handleRecruiterEmailBlur = (index: number) => {
      const email = recruiterEmails[index].trim();
      if (email && !emailRegex.test(email)) {
        setEmailError("Please enter a valid email address.");
      }
    };

    const addRecruiterEmail = () => {
      setRecruiterEmails((prev) => [...prev, ""]);
    };

    const removeRecruiterEmail = (index: number) => {
      setRecruiterEmails((prev) => prev.filter((_, i) => i !== index));
      setEmailError("");
    };

    const fieldSx = {
      "& .MuiOutlinedInput-root": {
        backgroundColor: "#fff",
        "& fieldset": { borderColor: "#d1d5db" },
        "&:hover fieldset": { borderColor: "#00d2aa" },
        "&.Mui-focused fieldset": { borderColor: "#00d2aa" },
      },
    };

    return (
      <Box className={styles.pressReleaseContent}>
        <Paper
          elevation={0}
          sx={{
            p: 4,
            backgroundColor: "#f8fffe",
            border: "2px solid #00d2aa",
            borderRadius: 3,
            textAlign: "center",
          }}
        >
          <CheckCircleIcon
            sx={{ fontSize: 64, color: "#00d2aa", mb: 2 }}
          />
          <Typography
            variant="h5"
            sx={{ mb: 2, color: "#333", fontWeight: 600 }}
          >
            {completionLabel}
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, color: "#666" }}>
            Enter email addresses to generate the job intake summary email.
          </Typography>

          <Box sx={{ maxWidth: 500, mx: "auto", textAlign: "left" }}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: "#444" }}>
              Recruiter Email(s) — To
            </Typography>
            {recruiterEmails.map((email, index) => (
              <Box key={index} sx={{ display: "flex", gap: 1, mb: 1.5 }}>
                <TextField
                  fullWidth
                  type="email"
                  placeholder="recruiter@example.com"
                  value={email}
                  onChange={(e) => updateRecruiterEmail(index, e.target.value)}
                  onBlur={() => handleRecruiterEmailBlur(index)}
                  error={!!emailError}
                  sx={fieldSx}
                  size="small"
                />
                {recruiterEmails.length > 1 && (
                  <Button
                    onClick={() => removeRecruiterEmail(index)}
                    sx={{ minWidth: 36, color: "#999", "&:hover": { color: "#e53e3e" } }}
                  >
                    ✕
                  </Button>
                )}
              </Box>
            ))}
            {emailError && (
              <Typography variant="caption" color="error" sx={{ display: "block", mb: 1 }}>
                {emailError}
              </Typography>
            )}
            <Button
              size="small"
              onClick={addRecruiterEmail}
              sx={{ mb: 3, color: "#00d2aa", textTransform: "none", pl: 0 }}
            >
              + Add another email
            </Button>

            <TextField
              fullWidth
              type="email"
              label="Hiring Manager Email (CC)"
              placeholder="hiring.manager@example.com"
              value={hiringManagerEmail}
              onChange={(e) => {
                setHiringManagerEmail(e.target.value);
                // Real-time: clear error only once the value becomes valid
                if (hiringManagerEmailError && emailRegex.test(e.target.value.trim())) {
                  setHiringManagerEmailError("");
                }
              }}
              onBlur={(e) => {
                const val = e.target.value.trim();
                if (!val) {
                  setHiringManagerEmailError("Please enter the hiring manager's email address.");
                } else if (!emailRegex.test(val)) {
                  setHiringManagerEmailError("Please enter a valid email address.");
                }
              }}
              error={!!hiringManagerEmailError}
              helperText={hiringManagerEmailError}
              sx={{ ...fieldSx, mb: 3 }}
            />

            <Box sx={{ textAlign: "center" }}>
              <Button
                variant="contained"
                onClick={handleEmailSubmit}
                disabled={loadingSummary}
                sx={{
                  backgroundColor: "#00d2aa",
                  color: "#fff",
                  px: 4,
                  py: 1.5,
                  fontSize: "16px",
                  "&:hover": { backgroundColor: "#00b894" },
                  "&:disabled": { backgroundColor: "#ccc" },
                }}
              >
                {loadingSummary ? "Generating..." : "Generate Summary"}
              </Button>
            </Box>
          </Box>
        </Paper>
      </Box>
    );
  };

  // ========== INTERVIEW ROUNDS RENDERER ==========

  const renderInterviewRoundsField = (q: SectionQuestion) => {
    let rounds: InterviewRound[] = [];
    const rawValue = sectionAnswers[q.field_id] ?? "";
    let hasStructuredRounds = false;

    try {
      if (rawValue) {
        const parsed = JSON.parse(rawValue);
        if (Array.isArray(parsed)) {
          rounds = normalizeInterviewRounds(parsed as InterviewRound[]);
          hasStructuredRounds = true;
        }
      }
    } catch {
      // Fall through to suggestion / fallback handling below.
    }

    if (!hasStructuredRounds && Array.isArray(q.suggestion)) {
      rounds = normalizeInterviewRounds(q.suggestion as InterviewRound[]);
      hasStructuredRounds = true;
    }

    if (!hasStructuredRounds && !rawValue.trim()) {
      rounds = normalizeInterviewRounds([]);
      hasStructuredRounds = true;
    }

    const updateRound = (index: number, field: "name" | "panel" | "evaluation", value: string) => {
      if (index === 0) return;

      const updated = [...rounds];
      if (field === "panel") {
        // trimStart removes unintentional leading spaces (e.g. after a comma) but
        // preserves trailing spaces and trailing empty segments so the user can
        // keep typing (e.g. "John Smith," stays intact). Cleanup happens onBlur.
        updated[index] = { ...updated[index], panel: value.split(",").map((s) => s.trimStart()) };
      } else {
        updated[index] = { ...updated[index], [field]: value };
      }
      setSectionAnswers((prev) => ({ ...prev, [q.field_id]: JSON.stringify(updated) }));
      if (sectionForwardStack.length > 0) setSectionForwardStack([]);
    };

    const removeRound = (index: number) => {
      if (index === 0) return;

      // Remove round and renumber remaining rounds sequentially
      const updated = normalizeInterviewRounds(rounds.filter((_, i) => i !== index));
      setSectionAnswers((prev) => ({ ...prev, [q.field_id]: JSON.stringify(updated) }));
      if (sectionForwardStack.length > 0) setSectionForwardStack([]);
    };

    const addRound = () => {
      const newRound: InterviewRound = {
        round: rounds.length + 1,
        name: "",
        panel: [],
        evaluation: "",
      };
      setSectionAnswers((prev) => ({ ...prev, [q.field_id]: JSON.stringify([...rounds, newRound]) }));
      if (sectionForwardStack.length > 0) setSectionForwardStack([]);
    };

    const fieldSx = { "& .MuiOutlinedInput-root": { backgroundColor: "#fff", "& fieldset": { borderColor: "#d1d5db" }, "&:hover fieldset": { borderColor: "#00d2aa" }, "&.Mui-focused fieldset": { borderColor: "#00d2aa" } } };

    const interviewRoundsHint = (
      <Typography variant="caption" sx={{ display: "block", mt: 0.75, color: "#888", fontStyle: "italic" }}>
        Typically 2 rounds for junior roles, 3 for managers, and more for senior roles.
      </Typography>
    );

    if (!hasStructuredRounds && rawValue.trim()) {
      return (
        <>
          <TextField
            fullWidth
            multiline
            minRows={2}
            value={rawValue}
            onChange={(e) => {
              setSectionAnswers((prev) => ({ ...prev, [q.field_id]: e.target.value }));
              if (sectionForwardStack.length > 0) setSectionForwardStack([]);
            }}
            placeholder="Enter interview rounds details..."
            disabled={loading}
            sx={fieldSx}
          />
          {interviewRoundsHint}
        </>
      );
    }

    return (
      <>
        <Box>
          {rounds.map((round, idx) => (
            <Box
              key={idx}
              sx={{ mb: 2, p: 2, border: "1px solid #e0e0e0", borderRadius: 2, backgroundColor: "#fff" }}
            >
              {idx === 0 ? (
                <>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 1.25 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#00d2aa" }}>
                      Round 1 — {round.name || DEFAULT_TA_SCREENING_ROUND.name}
                    </Typography>
                    <Chip
                      label="Default"
                      size="small"
                      sx={{ backgroundColor: "#e6f9f5", color: "#00a884", fontWeight: 700 }}
                    />
                  </Box>
                  <Typography variant="body2" sx={{ color: "#555", mb: 1.25 }}>
                    This round stays owned by Talent Acquisition and does not require hiring manager input.
                  </Typography>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 1.5,
                      backgroundColor: "#f8fffe",
                      border: "1px solid #d8f4ee",
                    }}
                  >
                    <Typography variant="caption" sx={{ fontWeight: 700, color: "#555", display: "block", mb: 0.5 }}>
                      What is evaluated
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#333" }}>
                      {round.evaluation || DEFAULT_TA_SCREENING_ROUND.evaluation}
                    </Typography>
                  </Box>
                </>
              ) : (
                <>
                  {/* Round header: number label + editable name + trash icon */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#00d2aa", flexShrink: 0 }}>
                      Round {idx + 1} —
                    </Typography>
                    <TextField
                      size="small"
                      value={round.name ?? ""}
                      onChange={(e) => updateRound(idx, "name", e.target.value)}
                      placeholder="e.g. Functional Interview, Leadership Discussion"
                      disabled={loading}
                      sx={{ flex: 1, ...fieldSx }}
                    />
                    <IconButton
                      onClick={() => removeRound(idx)}
                      disabled={loading}
                      size="small"
                      sx={{ color: "#ccc", flexShrink: 0, "&:hover": { color: "#e53e3e", backgroundColor: "#fff5f5" }, "&:disabled": { color: "#e0e0e0" } }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Box>

                  {/* Panel + Evaluation */}
                  <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                    <Box>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: "#555", mb: 0.5, display: "block" }}>
                        Panel (enter names)
                      </Typography>
                      <TextField
                        fullWidth
                        size="small"
                        value={round.panel?.join(", ") ?? ""}
                        onChange={(e) => updateRound(idx, "panel", e.target.value)}
                        onBlur={() => {
                          const cleaned = (round.panel ?? []).filter((s) => s.trim().length > 0);
                          const updated = [...rounds];
                          updated[idx] = { ...updated[idx], panel: cleaned };
                          setSectionAnswers((prev) => ({ ...prev, [q.field_id]: JSON.stringify(updated) }));
                        }}
                        disabled={loading}
                        sx={fieldSx}
                      />
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: "#555", mb: 0.5, display: "block" }}>
                        What is evaluated
                      </Typography>
                      <TextField
                        fullWidth
                        size="small"
                        value={round.evaluation ?? ""}
                        onChange={(e) => updateRound(idx, "evaluation", e.target.value)}
                        disabled={loading}
                        sx={fieldSx}
                      />
                    </Box>
                  </Box>
                </>
              )}
            </Box>
          ))}
        </Box>

        {/* Add round button */}
        <Button
          size="small"
          onClick={addRound}
          disabled={loading}
          sx={{ mt: 0.5, color: "#00d2aa", textTransform: "none", pl: 0, fontWeight: 600, "&:hover": { backgroundColor: "transparent", color: "#00b894" } }}
        >
          + Add Round
        </Button>

        {interviewRoundsHint}
      </>
    );
  };

  // ========== SCREENING QUESTIONS RENDERER ==========

  const renderScreeningQuestionsField = (q: SectionQuestion) => {
    let questions: ScreeningQuestion[] = [];
    try {
      const raw = sectionAnswers[q.field_id] ?? "";
      if (raw) questions = JSON.parse(raw);
    } catch {
      if (Array.isArray(q.suggestion)) questions = q.suggestion as ScreeningQuestion[];
    }

    const updateQuestion = (index: number, field: "question" | "reference_answer", value: string) => {
      const updated = [...questions];
      updated[index] = { ...updated[index], [field]: value };
      setSectionAnswers((prev) => ({ ...prev, [q.field_id]: JSON.stringify(updated) }));
      if (sectionForwardStack.length > 0) setSectionForwardStack([]);
    };

    if (questions.length === 0) {
      return (
        <TextField
          fullWidth
          multiline
          minRows={3}
          value={sectionAnswers[q.field_id] ?? ""}
          onChange={(e) => {
            setSectionAnswers((prev) => ({ ...prev, [q.field_id]: e.target.value }));
            if (sectionForwardStack.length > 0) setSectionForwardStack([]);
          }}
          placeholder="Enter screening questions..."
          disabled={loading}
          sx={{ "& .MuiOutlinedInput-root": { backgroundColor: "#fff", "& fieldset": { borderColor: "#d1d5db" }, "&:hover fieldset": { borderColor: "#00d2aa" }, "&.Mui-focused fieldset": { borderColor: "#00d2aa" } } }}
        />
      );
    }

    return (
      <Box>
        {questions.map((sq, idx) => (
          <Box
            key={idx}
            sx={{ mb: 2.5, p: 2.5, border: "1px solid #e0e0e0", borderRadius: 2, backgroundColor: "#fff" }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#00d2aa", mb: 1.5 }}>
              Question {idx + 1}
            </Typography>
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: "#555", mb: 0.5, display: "block" }}>
                Screening Question
              </Typography>
              <TextField
                fullWidth
                multiline
                minRows={2}
                value={sq.question ?? ""}
                onChange={(e) => updateQuestion(idx, "question", e.target.value)}
                disabled={loading}
                sx={{ "& .MuiOutlinedInput-root": { backgroundColor: "#fff", "& fieldset": { borderColor: "#d1d5db" }, "&:hover fieldset": { borderColor: "#00d2aa" }, "&.Mui-focused fieldset": { borderColor: "#00d2aa" } } }}
              />
            </Box>
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 600, color: "#555", mb: 0.5, display: "block" }}>
                Reference Answer (what to look for)
              </Typography>
              <TextField
                fullWidth
                multiline
                minRows={2}
                value={sq.reference_answer ?? ""}
                onChange={(e) => updateQuestion(idx, "reference_answer", e.target.value)}
                disabled={loading}
                sx={{ "& .MuiOutlinedInput-root": { backgroundColor: "#f9fffe", "& fieldset": { borderColor: "#d1d5db" }, "&:hover fieldset": { borderColor: "#00d2aa" }, "&.Mui-focused fieldset": { borderColor: "#00d2aa" } } }}
              />
            </Box>
          </Box>
        ))}
      </Box>
    );
  };

  const renderSourceChip = (source: string) => {
    const chips: Record<string, { label: string; bg: string; color: string }> = {
      "HM Required":      { label: "Required",       bg: "#ffebee", color: "#c62828" },
      "JD":               { label: "From JD",        bg: "#e3f2fd", color: "#1565c0" },
      "LLM (Generated)":  { label: "AI Suggested",   bg: "#f3e5f5", color: "#6a1b9a" },
      "System":           { label: "System default", bg: "#f5f5f5", color: "#555" },
    };
    const cfg = chips[source];
    if (!cfg) return null;
    return (
      <Chip
        label={cfg.label}
        size="small"
        sx={{ backgroundColor: cfg.bg, color: cfg.color, fontWeight: 600, fontSize: "11px", height: 20, flexShrink: 0 }}
      />
    );
  };

  const renderQAStage = () => {
    if (!currentSection) {
      return (
        <Box className={styles.pressReleaseContent}>
          {/* Progress bar skeleton */}
          <Skeleton variant="rectangular" height={8} sx={{ borderRadius: 4, mb: 1 }} />
          <Skeleton width="30%" height={14} sx={{ mb: 3 }} />
          {/* Question card skeleton */}
          <Paper elevation={0} sx={{ p: 3, mb: 3, backgroundColor: "#f8fffe", border: "1px solid #d8f4ee", borderRadius: 2 }}>
            {[1, 2].map((i) => (
              <Box key={i} sx={{ mb: i === 1 ? 3 : 0, pb: i === 1 ? 3 : 0, borderBottom: i === 1 ? "1px solid #e8f5f2" : "none" }}>
                <Skeleton width="65%" height={22} sx={{ mb: 1.5 }} />
                <Skeleton variant="rectangular" height={60} sx={{ borderRadius: 1 }} />
              </Box>
            ))}
          </Paper>
        </Box>
      );
    }

    const progressUnit = apiMode === "question" ? "Question" : "Section";
    const submitLabel = currentSection.is_last
      ? "Finish"
      : apiMode === "question"
        ? "Next"
        : "Submit Section";
    const completedSectionTrail = sectionHistory.filter((entry, idx, history) =>
      history.findIndex(
        (candidate) => candidate.section.section === entry.section.section
      ) === idx
    );

    return (
      <Box className={styles.pressReleaseContent} ref={sectionTopRef}>
        {/* Job title anchor */}
        {jobTitle && (
          <Typography
            variant="subtitle2"
            sx={{ color: "#888", fontWeight: 500, mb: 1.5, letterSpacing: 0.3 }}
          >
            {jobTitle}
          </Typography>
        )}

        {/* Completed sections breadcrumb */}
        {completedSectionTrail.length > 0 && (
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
            {completedSectionTrail.map((entry, idx) => (
              <Chip
                key={idx}
                icon={<CheckCircleIcon sx={{ fontSize: "14px !important", color: "#00a884 !important" }} />}
                label={entry.section.section}
                size="small"
                sx={{
                  backgroundColor: "#e6f9f5",
                  color: "#00a884",
                  fontWeight: 500,
                  fontSize: "11px",
                  height: 24,
                }}
              />
            ))}
          </Box>
        )}

        {/* Progress Header */}
        <Box sx={{ mb: 3 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
            }}
          >
            <Typography variant="body2" sx={{ color: "#666", fontWeight: 500 }}>
              {progressUnit} {currentSection.section_index + 1} of{" "}
              {currentSection.total_sections}
            </Typography>
            <Chip
              label={currentSection.section}
              size="small"
              sx={{
                backgroundColor: "#e6f9f5",
                color: "#00d2aa",
                fontWeight: 600,
              }}
            />
          </Box>
          <LinearProgress
            variant="determinate"
            value={currentSection.progress_percent}
            sx={{
              height: 8,
              borderRadius: 4,
              backgroundColor: "#e0e0e0",
              "& .MuiLinearProgress-bar": {
                backgroundColor: "#00d2aa",
                borderRadius: 4,
              },
            }}
          />
          <Typography
            variant="caption"
            sx={{ color: "#999", display: "block", mt: 0.5 }}
          >
            {currentSection.progress_percent.toFixed(1)}% complete
          </Typography>
        </Box>

        {/* Questions List */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            backgroundColor: "#f8fffe",
            border: "1px solid #d8f4ee",
            borderRadius: 2,
          }}
        >
          {/* Required fields legend */}
          {currentSection.questions.some(
            (q) => isVisibleJobIntakeQuestionField(q.field_id) && q.source === "HM Required"
          ) && (
            <Typography variant="caption" sx={{ display: "block", mb: 2.5, color: "#c62828", fontWeight: 500 }}>
              <span style={{ fontWeight: 700 }}>*</span> Required — must be filled before proceeding
            </Typography>
          )}

          {currentSection.questions
            .filter((q) => isVisibleJobIntakeQuestionField(q.field_id))
            .map((q, idx, visibleQuestions) => {
            const isRequired = q.source === "HM Required";
            const hasError = !!sectionErrors[q.field_id];
            const isLast = idx === visibleQuestions.length - 1;

            return (
              <Box
                key={q.field_id}
                sx={{
                  mb: isLast ? 0 : 3,
                  pb: isLast ? 0 : 3,
                  borderBottom: isLast ? "none" : "1px solid #e8f5f2",
                  pl: hasError ? 1.5 : 0,
                  borderLeft: hasError ? "3px solid #d32f2f" : "none",
                }}
              >
                {/* Question label row */}
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1.5, gap: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5, flex: 1 }}>
                    <Typography variant="subtitle1" sx={{ color: "#222", fontWeight: 600, lineHeight: 1.4 }}>
                      {q.question}
                      {isRequired && (
                        <Typography component="span" sx={{ color: "#d32f2f", ml: 0.5, fontWeight: 700 }}>*</Typography>
                      )}
                    </Typography>
                    {fieldHints[q.field_id] && (
                      <Tooltip
                        title={fieldHints[q.field_id]}
                        placement="top"
                        arrow
                        componentsProps={{
                          tooltip: {
                            sx: {
                              bgcolor: "#1a1a2e",
                              color: "#f0f0f0",
                              fontSize: "12px",
                              lineHeight: 1.6,
                              p: 1.5,
                              maxWidth: 320,
                              "& .MuiTooltip-arrow": { color: "#1a1a2e" },
                            },
                          },
                        }}
                      >
                        <InfoOutlinedIcon
                          sx={{
                            fontSize: 16,
                            color: "#bbb",
                            cursor: "default",
                            mt: 0.4,
                            flexShrink: 0,
                            transition: "color 0.15s",
                            "&:hover": { color: "#00d2aa" },
                          }}
                        />
                      </Tooltip>
                    )}
                  </Box>
                  {renderSourceChip(q.source)}
                </Box>

                {q.field_id === "target_start_date" ? (
                  <>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        label="Target Start Date"
                        value={sectionAnswers[q.field_id] ? dayjs(sectionAnswers[q.field_id]) : null}
                        onChange={(newValue: Dayjs | null) => {
                          const formatted = newValue ? newValue.format("YYYY-MM-DD") : "";
                          setSectionAnswers((prev) => ({ ...prev, [q.field_id]: formatted }));
                          if (sectionErrors[q.field_id]) setSectionErrors((prev) => { const n = { ...prev }; delete n[q.field_id]; return n; });
                          if (sectionForwardStack.length > 0) setSectionForwardStack([]);
                        }}
                        disabled={loading}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            error: hasError,
                            helperText: hasError ? sectionErrors[q.field_id] : undefined,
                            placeholder: "Select a date...",
                            sx: {
                              "& .MuiOutlinedInput-root": {
                                backgroundColor: "#fff",
                                "& fieldset": { borderColor: hasError ? "#d32f2f" : "#d1d5db" },
                                "&:hover fieldset": { borderColor: "#00d2aa" },
                                "&.Mui-focused fieldset": { borderColor: hasError ? "#d32f2f" : "#00d2aa" },
                              },
                            },
                          },
                        }}
                      />
                    </LocalizationProvider>
                    <Typography
                      variant="caption"
                      sx={{ display: "block", mt: 0.75, color: "#888", fontStyle: "italic" }}
                    >
                      Hiring typically takes 3–6 months including sourcing, interviews, approvals, and notice period.
                    </Typography>
                  </>
                ) : q.field_id === "interview_rounds" ? (
                  renderInterviewRoundsField(q)
                ) : q.field_id === "screening_questions" ? (
                  renderScreeningQuestionsField(q)
                ) : (
                  <TextField
                    fullWidth
                    multiline
                    minRows={isRequired ? 2 : 2}
                    value={sectionAnswers[q.field_id] ?? ""}
                    onChange={(e) => {
                      setSectionAnswers((prev) => ({ ...prev, [q.field_id]: e.target.value }));
                      if (hasError) setSectionErrors((prev) => { const n = { ...prev }; delete n[q.field_id]; return n; });
                      if (sectionForwardStack.length > 0) setSectionForwardStack([]);
                    }}
                    placeholder={isRequired ? "Required — please enter your response to continue..." : "Enter your answer or leave to skip..."}
                    disabled={loading}
                    error={hasError}
                    helperText={hasError ? sectionErrors[q.field_id] : undefined}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "#fff",
                        "& fieldset": { borderColor: hasError ? "#d32f2f" : "#d1d5db" },
                        "&:hover fieldset": { borderColor: "#00d2aa" },
                        "&.Mui-focused fieldset": { borderColor: hasError ? "#d32f2f" : "#00d2aa" },
                      },
                    }}
                  />
                )}
              </Box>
            );
          })}
        </Paper>

        {/* Error Alert with retry */}
        {qaError && (
          <Alert
            severity="error"
            sx={{ mb: 3 }}
            action={
              sessionId ? (
                <Button
                  color="error"
                  size="small"
                  onClick={() => {
                    setQaError("");
                    fetchNextSection(sessionId, apiMode);
                  }}
                >
                  Retry
                </Button>
              ) : undefined
            }
          >
            {qaError}
          </Alert>
        )}

        {/* Navigation Buttons */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Button
            variant="outlined"
            onClick={handlePreviousSection}
            disabled={sectionHistory.length === 0 || loading}
            startIcon={<ArrowBackIcon />}
            sx={{
              borderColor: "#00d2aa",
              color: "#00d2aa",
              px: 3,
              py: 1.5,
              "&:hover": { borderColor: "#00b894", backgroundColor: "#f0fffe" },
              "&:disabled": { borderColor: "#ccc", color: "#ccc" },
            }}
          >
            Previous
          </Button>

          <Button
            variant="contained"
            onClick={handleSubmitSection}
            disabled={loading}
            endIcon={<ArrowForwardIcon />}
            sx={{
              backgroundColor: "#00d2aa",
              color: "#fff",
              px: 3,
              py: 1.5,
              "&:hover": { backgroundColor: "#00b894" },
              "&:disabled": { backgroundColor: "#ccc" },
            }}
          >
            {loading
              ? "Processing..."
              : submitLabel}
          </Button>
        </Box>
      </Box>
    );
  };

  const renderSummaryStage = () => {
    if (loadingSummary) {
      return (
        <Box className={styles.pressReleaseContent}>
          <Skeleton width="55%" height={44} sx={{ mb: 1 }} />
          <Skeleton width="35%" height={26} sx={{ mb: 4 }} />
          {[1, 2, 3].map((i) => (
            <Paper key={i} elevation={0} sx={{ p: 3, mb: 3, border: "1px solid #e0e0e0", borderRadius: 3 }}>
              <Skeleton width="45%" height={28} sx={{ mb: 2.5 }} />
              {[1, 2, 3].map((j) => (
                <Box key={j} sx={{ mb: 2.5 }}>
                  <Skeleton width="32%" height={16} sx={{ mb: 0.75 }} />
                  <Skeleton variant="rectangular" height={44} sx={{ borderRadius: 1 }} />
                </Box>
              ))}
            </Paper>
          ))}
        </Box>
      );
    }

    if (summaryError) {
      return (
        <Box className={styles.pressReleaseContent}>
          <Alert
            severity="error"
            sx={{ mb: 3 }}
            action={
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  color="error"
                  size="small"
                  onClick={retrySummaryFetch}
                  disabled={!sessionId}
                >
                  Retry
                </Button>
                <Button
                  color="error"
                  size="small"
                  onClick={() => setStage("email_capture")}
                >
                  Back
                </Button>
              </Box>
            }
          >
            {summaryError}
          </Alert>
        </Box>
      );
    }

    if (summaryData.length === 0) {
      return (
        <Box className={styles.pressReleaseContent}>
          <Alert
            severity="info"
            sx={{ mb: 3 }}
            action={
              <Button
                color="info"
                size="small"
                onClick={() => setStage("email_capture")}
              >
                Back
              </Button>
            }
          >
            No summary content was returned for this session yet.
          </Alert>
        </Box>
      );
    }

    return (
      <Box className={styles.pressReleaseContent}>
        {/* Header row: title + actions */}
        <Box sx={{ mb: 3, pb: 3, borderBottom: "2px solid #e0e0e0", display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: "#00d2aa", mb: 0.5 }}>
              Job Intake Summary
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 500, color: "#333" }}>
              {jobTitle}
            </Typography>
          </Box>
        </Box>

        {/* Sticky section jump-nav */}
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 10,
            backgroundColor: "#fff",
            pb: 1.5,
            pt: 1,
            mb: 2,
            borderBottom: "1px solid #e8f5f2",
            display: "flex",
            gap: 0.75,
            flexWrap: "wrap",
          }}
        >
          {summaryData.map((section) => (
            <Chip
              key={section.section_name}
              label={section.section_name}
              size="small"
              onClick={() =>
                sectionRefs.current[section.section_name]?.scrollIntoView({
                  behavior: "smooth",
                  block: "start",
                })
              }
              sx={{
                cursor: "pointer",
                backgroundColor: "#f0fffe",
                color: "#00a884",
                fontWeight: 500,
                fontSize: "11px",
                "&:hover": { backgroundColor: "#d4f5ee" },
              }}
            />
          ))}
        </Box>

        {/* All Sections */}
        {summaryData.map((section, sectionIdx) => (
          <Paper
            key={section.section_name}
            ref={(el) => { sectionRefs.current[section.section_name] = el as HTMLDivElement | null; }}
            elevation={0}
            sx={{
              p: 3,
              mb: 3,
              backgroundColor: "#f8fffe",
              border: "1px solid #e0e0e0",
              borderRadius: 3,
              scrollMarginTop: 60,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                mb: 2.5,
                color: "#00d2aa",
                fontWeight: 600,
                borderBottom: "2px solid #00d2aa",
                pb: 1,
              }}
            >
              {section.section_name}
            </Typography>

            {section.fields.map((field, fieldIdx) => {
              const isEditing =
                editingField?.sectionIdx === sectionIdx &&
                editingField?.fieldIdx === fieldIdx;
              return (
                <Box key={field.field_id} sx={{ mb: 2.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#333", mb: 0.75 }}>
                    {field.field_label}
                  </Typography>
                  {isEditing ? (
                    <TextField
                      multiline
                      fullWidth
                      autoFocus
                      value={editingValue}
                      onChange={(e) => setEditingValue(e.target.value)}
                      onBlur={handleSaveFieldEdit}
                      size="small"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          fontSize: "14px",
                          backgroundColor: "#fff",
                          "& fieldset": { borderColor: "#00d2aa" },
                          "&:hover fieldset": { borderColor: "#00b894" },
                          "&.Mui-focused fieldset": { borderColor: "#00d2aa" },
                        },
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1,
                        backgroundColor: field.value ? "#fff" : "#fafafa",
                        p: 1.5,
                        borderRadius: 2,
                        border: `1px solid ${field.value ? "#e0e0e0" : "#f0f0f0"}`,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          flex: 1,
                          color: field.value ? "#555" : "#aaa",
                          whiteSpace: "pre-wrap",
                          fontStyle: field.value ? "normal" : "italic",
                        }}
                      >
                        {field.value || "Not provided"}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setEditingField({ sectionIdx, fieldIdx });
                          setEditingValue(field.value || "");
                        }}
                        sx={{
                          color: "#9ca3af",
                          p: 0.25,
                          flexShrink: 0,
                          "&:hover": { color: "#6b7280", backgroundColor: "transparent" },
                        }}
                      >
                        <EditIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Box>
                  )}
                </Box>
              );
            })}
          </Paper>
        ))}

        {/* Bottom actions */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, mt: 2 }}>
          <Button
            variant="contained"
            onClick={handleDraftEmail}
            sx={{
              backgroundColor: "#00d2aa",
              color: "#fff",
              px: 4,
              py: 1.5,
              "&:hover": { backgroundColor: "#00b894" },
            }}
          >
            Draft Email
          </Button>
        </Box>
      </Box>
    );
  };

  // ========== EMAIL PREVIEW STAGE ==========

  const renderEmailPreviewStage = () => {
    if (!emailData) {
      return (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <CircularProgress sx={{ color: "#00d2aa" }} />
          <Typography variant="body1" sx={{ mt: 2, color: "#666" }}>
            Loading email...
          </Typography>
        </Box>
      );
    }

    // Strip the redundant title/date header lines emitted by the backend,
    // then prepend a formal greeting before the numbered sections.
    const buildProcessedBody = () => {
      const lines = emailData.body.split("\n").filter(
        (l) => !l.trim().startsWith("Job Intake Summary —") && !l.trim().startsWith("Date:")
      );
      while (lines.length > 0 && !lines[0].trim()) lines.shift();
      const greeting = [
        `Dear Hiring Team,`,
        ``,
        `I hope this message finds you well. Please find below the completed job intake summary for the ${jobTitle || "open"} position. Kindly review the details and let us know if you have any feedback or amendments.`,
        ``,
      ];
      return [...greeting, ...lines].join("\n");
    };
    const processedBody = buildProcessedBody();

    const fullEmailText = `To: ${emailData.to}\n${emailData.cc ? `CC: ${emailData.cc}\n` : ""}Subject: ${emailData.subject}\n\n${processedBody}`;

    const handleDownload = () => {
      const fileName = `job-intake-summary${jobTitle ? `-${jobTitle.replace(/\s+/g, "-")}` : ""}`;

      // Parse the body lines into structured HTML for a professional Word document
      const bodyLines = processedBody.split("\n");
      let bodyHtml = "";

      const md2html = (s: string) => s.replace(/\*\*(.+?)\*\*/g, "<b>$1</b>");

      for (const line of bodyLines) {
        const t = line.trim();
        if (!t) { bodyHtml += `<p style="margin:0;line-height:6pt">&nbsp;</p>`; continue; }

        // Numbered section headers "1. ROLE OVERVIEW"
        if (/^\d+\.\s+[A-Z &]+$/.test(t)) {
          bodyHtml += `
            <table width="100%" cellpadding="0" cellspacing="0" style="margin:20px 0 8px 0">
              <tr>
                <td style="background:#007A66;padding:6px 12px;color:#fff;font-size:10.5pt;font-weight:bold;letter-spacing:0.5px">
                  ${t}
                </td>
              </tr>
            </table>`;
          continue;
        }

        // Interview round "    Round X – ..."
        if (/^Round\s+\d+/.test(t)) {
          bodyHtml += `<p style="margin:3px 0 3px 48px;font-size:9.5pt;color:#333">${md2html(t)}</p>`;
          continue;
        }

        // Sub-bullet "    • item"
        if (/^ {4}•/.test(line)) {
          bodyHtml += `<p style="margin:2px 0 2px 48px;font-size:10pt">&#9702;&nbsp;&nbsp;${md2html(line.replace(/^ +•\s*/, ""))}</p>`;
          continue;
        }

        // Top-level bullet "  • item"
        if (/^ {2}•/.test(line)) {
          bodyHtml += `<p style="margin:3px 0 3px 24px;font-size:10.5pt">&#8226;&nbsp;&nbsp;${md2html(line.replace(/^ +•\s*/, ""))}</p>`;
          continue;
        }

        // Screening question "  Q1: ..."
        if (/^Q\d+:/.test(t)) {
          bodyHtml += `<p style="margin:10px 0 3px 24px;font-weight:bold;color:#007A66;font-size:10.5pt">${md2html(t)}</p>`;
          continue;
        }

        // Screening reference answer "     → ..."
        if (t.startsWith("→")) {
          bodyHtml += `<p style="margin:2px 0 10px 36px;color:#555;font-style:italic;font-size:10pt">${md2html(t)}</p>`;
          continue;
        }

        bodyHtml += `<p style="margin:3px 0;font-size:10.5pt">${md2html(t)}</p>`;
      }

      const html = `
        <html xmlns:o="urn:schemas-microsoft-com:office:office"
              xmlns:w="urn:schemas-microsoft-com:office:word"
              xmlns="http://www.w3.org/TR/REC-html40">
        <head>
          <meta charset="utf-8">
          <!--[if gte mso 9]><xml>
            <w:WordDocument>
              <w:View>Print</w:View>
              <w:Zoom>100</w:Zoom>
              <w:DoNotOptimizeForBrowser/>
            </w:WordDocument>
          </xml><![endif]-->
        </head>
        <body style="font-family:Calibri,Arial,sans-serif;color:#222;margin:0;padding:0">

          <!-- Page wrapper -->
          <div style="max-width:750px;margin:0 auto;padding:40px 50px">

            <!-- Document header banner -->
            <table width="100%" cellpadding="0" cellspacing="0" style="margin-bottom:24px">
              <tr>
                <td style="background:#007A66;padding:16px 20px;vertical-align:middle">
                  <p style="margin:0;color:#fff;font-size:22pt;font-weight:bold;letter-spacing:1px">G42 AI Foundry</p>
                  <p style="margin:4px 0 0 0;color:#b2f0e8;font-size:11pt">Talent Acquisition — Job Intake Summary</p>
                </td>
                <td width="120" style="background:#005a4d;padding:16px 20px;text-align:right;vertical-align:middle">
                  <p style="margin:0;color:#b2f0e8;font-size:8pt">CONFIDENTIAL</p>
                </td>
              </tr>
            </table>

            <!-- Email metadata table -->
            <table width="100%" cellpadding="6" cellspacing="0"
                   style="border:1px solid #cce8e4;margin-bottom:24px;font-size:10pt">
              <tr style="background:#f0faf8">
                <td width="80" style="font-weight:bold;color:#007A66;border-bottom:1px solid #cce8e4">To</td>
                <td style="border-bottom:1px solid #cce8e4">${emailData.to || "—"}</td>
              </tr>
              ${emailData.cc ? `
              <tr>
                <td width="80" style="font-weight:bold;color:#007A66;border-bottom:1px solid #cce8e4">CC</td>
                <td style="border-bottom:1px solid #cce8e4">${emailData.cc}</td>
              </tr>` : ""}
              <tr>
                <td style="font-weight:bold;color:#007A66;border-bottom:1px solid #cce8e4">Subject</td>
                <td style="border-bottom:1px solid #cce8e4">${emailData.subject || "—"}</td>
              </tr>
              <tr style="background:#f0faf8">
                <td style="font-weight:bold;color:#007A66">Date</td>
                <td>${emailData.completed_at || new Date().toISOString().split("T")[0]}</td>
              </tr>
            </table>

            <!-- Body content -->
            ${bodyHtml}

            <!-- Footer -->
            <table width="100%" cellpadding="0" cellspacing="0" style="margin-top:40px;border-top:1px solid #cce8e4">
              <tr>
                <td style="padding-top:10px;font-size:8.5pt;color:#aaa;text-align:center">
                  Generated by G42 AI Foundry Talent Acquisition Agent &nbsp;|&nbsp; Confidential — for internal use only
                </td>
              </tr>
            </table>

          </div>
        </body>
        </html>`;

      // Record download action - fire and forget (non-blocking)
      // Send the actual HTML content that's being downloaded
      dispatch(
        recordAgentAction({
          agentId: numericAgentId,
          actionType: "download",
          content: JSON.stringify({
            fileName: `${fileName}.doc`,
            documentContent: html,
          }),
        })
      );

      const blob = new Blob(["\ufeff" + html], { type: "application/msword;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${fileName}.doc`;
      a.click();
      URL.revokeObjectURL(url);
    };

    return (
      <Box className={styles.pressReleaseContent}>
        <Box
          sx={{
            border: "1px solid #ddd",
            borderRadius: "8px",
            padding: 2.5,
            backgroundColor: "#fafafa",
            transition: "all 0.2s ease-in-out",
            position: "relative",
            "&:hover": {
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
            },
          }}
        >
          {/* Email Header */}
          <Box
            sx={{
              marginBottom: 1,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
            }}
          >
            <Box>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                Pre-Drafted Summary Email
              </Typography>
              <Typography variant="body2" color="textSecondary" component="div" sx={{ lineHeight: 1.6 }}>
                <strong>To:</strong> {emailData.to}
              </Typography>
              {emailData.cc && (
                <Typography variant="body2" color="textSecondary" component="div" sx={{ lineHeight: 1.6 }}>
                  <strong>CC:</strong> {emailData.cc}
                </Typography>
              )}
            </Box>

            {/* Action Buttons - Top Right */}
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<FileDownloadIcon />}
                onClick={handleDownload}
                sx={{
                  borderColor: "#00d2aa",
                  color: "#00d2aa",
                  paddingX: 2,
                  paddingY: 1,
                  fontSize: "14px",
                  fontWeight: 600,
                  textTransform: "none",
                  "&:hover": { borderColor: "#00b894", backgroundColor: "#f0fffe" },
                }}
              >
                Download
              </Button>
              <Button
                variant="contained"
                size="small"
                startIcon={<ContentCopyIcon />}
                onClick={() => {
                  navigator.clipboard.writeText(fullEmailText);
                }}
                sx={{
                  backgroundColor: "#00d2aa",
                  color: "#fff",
                  paddingX: 2,
                  paddingY: 1,
                  fontSize: "14px",
                  fontWeight: 600,
                  textTransform: "none",
                  "&:hover": { backgroundColor: "#00b894" },
                }}
              >
                Copy Email
              </Button>
            </Box>
          </Box>

          <Divider sx={{ my: 1.5 }} />

          {/* Subject */}
          <Box sx={{ margin: "12px 0" }}>
            <Typography variant="body2" component="span" fontWeight={600}>
              Subject:{" "}
            </Typography>
            <Typography variant="body2" component="span">
              {emailData.subject}
            </Typography>
          </Box>

          <Divider sx={{ my: 1.5 }} />

          {/* Body */}
          <Box sx={{ margin: "12px 0", fontSize: "14px", lineHeight: 1.6 }}>
            <MarkdownRenderer content={processedBody.split("\n").map((line) =>
              /^\d+\.\s+[A-Z &]+$/.test(line.trim()) ? `\n**${line.trim()}**` : line
            ).join("\n")} />
          </Box>
        </Box>

        {/* Back to Summary Button */}
        <Box sx={{ mt: 3, textAlign: "center" }}>
          <Button
            variant="outlined"
            onClick={() => setStage("summary")}
            sx={{
              borderColor: "#00d2aa",
              color: "#00d2aa",
              "&:hover": { borderColor: "#00b894", backgroundColor: "#f0fffe" },
            }}
          >
            Back to Summary
          </Button>
        </Box>
      </Box>
    );
  };

  // ========== MAIN RENDER ==========

  if (workflowType === "old") {
    return renderOldWorkflow();
  }

  const STEPS = ["Upload", "Q&A", "Review", "Email"];

  return (
    <>
      {/* Stage stepper */}
      <Stepper
        activeStep={getActiveStep()}
        alternativeLabel
        sx={{
          mb: 3,
          "& .MuiStepLabel-label": { fontSize: "12px", mt: 0.5 },
          "& .MuiStepIcon-root.Mui-active": { color: "#00d2aa" },
          "& .MuiStepIcon-root.Mui-completed": { color: "#00d2aa" },
        }}
      >
        {STEPS.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {stage === "qa" && renderQAStage()}
      {stage === "email_capture" && renderEmailCaptureStage()}
      {stage === "summary" && renderSummaryStage()}
      {stage === "email_preview" && renderEmailPreviewStage()}

      {/* Section saved confirmation */}
      <Snackbar
        open={sectionSubmitSuccess}
        autoHideDuration={2000}
        onClose={() => setSectionSubmitSuccess(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSectionSubmitSuccess(false)}
          severity="success"
          sx={{ width: "100%", backgroundColor: "#e6f9f5", color: "#004d40", "& .MuiAlert-icon": { color: "#00d2aa" } }}
        >
          {apiMode === "question"
            ? "Question saved successfully"
            : "Section saved successfully"}
        </Alert>
      </Snackbar>
    </>
  );
}
